/**
 * Helpers for the Agency "Ad Serving Settings" S3/FTP source folder paths.
 *
 * A stored path looks like `cd-ftp-files/incoming/<folderName>/<suffix>/`, where
 * `<folderName>` is the agency's S3 folder (e.g. `strongauto`) and `<suffix>` is
 * the asset subfolder (e.g. `dynamicvideos`).
 *
 * Historically the `<folderName>` segment was always derived from the agency name,
 * which broke agencies whose real S3 folder was created with a different name
 * (COGPLAT-2855). These helpers let the UI preserve an already-stored folder name
 * instead of regenerating it from the agency name on edit, while still defaulting
 * to the name for brand-new setups.
 */

export const AD_SERVING_PREFIX = 'cd-ftp-files/incoming/';

/**
 * Normalize an agency name into a default S3 folder segment.
 * e.g. "Strong Automotive" -> "strong-automotive"
 */
export function normalizeAgencyFolderName(agencyName: string | null | undefined): string {
    return (agencyName || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

/**
 * Extract the folder segment (the part immediately after the prefix) from a
 * stored path. Returns null when the path is empty or does not use the prefix.
 * e.g. "cd-ftp-files/incoming/strongauto/dynamicvideos/" -> "strongauto"
 */
export function extractFolderName(path: string | null | undefined): string | null {
    if (!path || !path.startsWith(AD_SERVING_PREFIX)) return null;
    const rest = path.substring(AD_SERVING_PREFIX.length);
    const slashIndex = rest.indexOf('/');
    if (slashIndex <= 0) return null;
    return rest.substring(0, slashIndex);
}

/**
 * Extract the suffix (the part after `<prefix>/<folderName>/`) from a stored
 * path, given the folder segment currently in effect. If the path does not start
 * with that prefix, the full path is returned unchanged (matching the prior UI
 * fallback behavior).
 * e.g. ("cd-ftp-files/incoming/strongauto/dynamicvideos/", "strongauto") -> "dynamicvideos/"
 */
export function extractSuffix(path: string | null | undefined, folderName: string): string {
    if (!path) return '';
    const prefix = `${AD_SERVING_PREFIX}${folderName}/`;
    if (path.startsWith(prefix)) {
        return path.substring(prefix.length);
    }
    return path;
}
